import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Users, Crown, FileImage, BadgeCheck, ShieldCheck, Wallet,
  LayoutDashboard, UserCog, Flag, Sparkles, LogOut, Ban, CheckCircle2, DollarSign, Lock,
  Images, Trash2, Unlock, Film, ShieldAlert, FileText, MessageCircle,
} from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import LuvmeIcon from "@/components/LuvmeIcon";
import AdminGrowthChart from "@/components/AdminGrowthChart";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

const TABS = [
  { id: "overview", label: "Panoramica", icon: LayoutDashboard },
  { id: "users", label: "Utenti", icon: UserCog },
  { id: "moderation", label: "Moderazione", icon: Flag },
  { id: "content", label: "Contenuti", icon: Images },
  { id: "verification", label: "Verifiche", icon: BadgeCheck },
  { id: "finance", label: "Finanze", icon: Wallet },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [commentsFor, setCommentsFor] = useState(null);
  const [commentList, setCommentList] = useState([]);

  const openComments = async (p) => {
    if (commentsFor === p.id) { setCommentsFor(null); return; }
    setCommentsFor(p.id);
    setCommentList([]);
    try {
      const { data } = await api.get(`/posts/${p.id}/comments`);
      setCommentList(data.comments);
    } catch (e) {}
  };
  const delComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setCommentList((c) => c.filter((x) => x.id !== id));
      toast.success("Commento eliminato");
    } catch (e) { toast.error("Errore"); }
  };
  const [loading, setLoading] = useState(true);
  const [growth, setGrowth] = useState([]);
  const [billing, setBilling] = useState([]);

  const exportBilling = () => {
    const rows = [["Creator", "Modalità", "Intestatario", "PIVA/CF", "Indirizzo", "Paese", "IBAN"]];
    billing.forEach((b) => {
      const d = b.billing || {};
      const mode = b.mode === "vat" ? "A - P.IVA" : b.mode === "occasional" ? "B - Occasionale" : "";
      rows.push([b.display_name, mode, d.legal_name || d.name || "", d.vat_number || d.tax_code || "", d.address || d.billing_address || "", d.country || "", d.iban || ""]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "luvme-dati-fiscali.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV generato");
  };

  const load = async () => {
    try {
    const [s, u, p, v] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/posts"),
      api.get("/admin/verifications").catch(() => ({ data: [] })),
    ]);
    setStats(s.data);
    setUsers(u.data);
    setPosts(p.data);
    setVerifications(v.data);
    api.get("/admin/stats/timeseries").then(({ data }) => setGrowth(data)).catch(() => {});
    api.get("/admin/creators/billing").then(({ data }) => setBilling(data)).catch(() => {});
    setLoading(false);
      } catch (e) {
      toast.error("Impossibile caricare i dati. Controlla la connessione e riprova.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approveVerif = async (v) => {
    try {
      await api.post(`/admin/creators/${v.profile_id}/verification/approve`);
      toast.success(`${v.display_name} verificato`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const rejectVerif = async () => {
    if (!rejectReason.trim()) { toast.error("Indica un motivo per il rifiuto"); return; }
    try {
      await api.post(`/admin/creators/${rejectFor.profile_id}/verification/reject`, { reason: rejectReason.trim() });
      toast.success(`Verifica di ${rejectFor.display_name} rifiutata`);
      setRejectFor(null);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const deletePost = async (post) => {
    try {
      await api.delete(`/admin/posts/${post.id}`);
      setPosts((list) => list.filter((x) => x.id !== post.id));
      if (stats) setStats({ ...stats, posts: Math.max(0, stats.posts - 1) });
      toast.success("Contenuto rimosso");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleSuspend = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/suspend`);
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, suspended: data.suspended } : x)));
      toast.success(data.suspended ? "Account sospeso" : "Sospensione revocata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleVerify = async (u) => {
    if (!u.profile_id) { toast.error("Il creator non ha ancora un profilo"); return; }
    try {
      const { data } = await api.post(`/admin/creators/${u.profile_id}/verify`);
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, is_verified: data.is_verified } : x)));
      if (stats) setStats({ ...stats, verified_creators: stats.verified_creators + (data.is_verified ? 1 : -1) });
      toast.success(data.is_verified ? "Badge verificato assegnato" : "Verifica rimossa");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const creators = users.filter((u) => u.role === "creator");
  const fans = users.filter((u) => u.role === "fan");

  return (
    <div className="flex bg-[#0D0D0D] min-h-screen text-white overflow-x-hidden">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex sticky top-0 h-screen w-64 shrink-0 border-r border-white/10 bg-[#0D0D0D] flex-col p-5">
        <div className="flex items-center gap-2 px-2 py-4 mb-2">
          <LuvmeIcon className="w-7 h-7 sm:w-8 sm:h-8" />
          <div>
            <p className="text-xl font-black font-heading leading-none">Luvme</p>
            <p className="text-[10px] text-[#F5C6D6] uppercase tracking-[0.2em] mt-1">Admin</p>
          </div>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {TABS.map((t) => (
            <button
              key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-300 font-medium text-left ${
                tab === t.id ? "bg-[#E91E8C] text-white" : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon className="w-5 h-5" />
              <span>{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-4 mt-4">
          <div className="px-2 mb-3">
            <p className="text-sm font-semibold truncate">{user?.display_name}</p>
            <p className="text-xs text-white/50">Amministratore</p>
          </div>
          <Button onClick={doLogout} variant="ghost" data-testid="admin-logout" className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/5 rounded-xl">
            <LogOut className="w-5 h-5" /><span>Esci</span>
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-5 sm:p-8 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <LuvmeIcon className="w-6 h-6 sm:w-7 sm:h-7" />
            <span className="text-lg font-black font-heading">Luvme <span className="text-[10px] text-[#F5C6D6] uppercase tracking-widest align-middle">Admin</span></span>
          </div>
          <Button onClick={doLogout} variant="ghost" size="sm" data-testid="admin-logout-mobile" className="gap-1.5 text-white/70 hover:text-white hover:bg-white/5 rounded-full">
            <LogOut className="w-4 h-4" /> Esci
          </Button>
        </div>
        {/* Mobile tab bar — 2-column grid (no horizontal cutoff) */}
        <div className="md:hidden grid grid-cols-2 gap-2.5 p-3 bg-[#1A1A1A] border border-white/10 rounded-2xl mb-6">
          {TABS.map((t) => (
            <button
              key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}-mobile`}
              className={`flex items-center gap-2 px-3.5 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                tab === t.id ? "bg-[#E91E8C] text-white shadow-[0_0_20px_rgba(233,30,140,0.35)]" : "bg-white/5 text-white/70 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4 shrink-0" /> <span className="truncate">{t.label}</span>
            </button>
          ))}
        </div>
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#E91E8C] animate-spin" /></div>
        ) : (
          <div className="max-w-5xl">
            <h1 className="text-3xl font-black font-heading mb-1">{TABS.find((t) => t.id === tab)?.label}</h1>
            <p className="text-white/50 mb-8 text-sm">Pannello di amministrazione della piattaforma Luvme.</p>

            {tab === "overview" && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-overview">
                {[
                  { icon: Crown, label: "Creator attivi", value: stats.creators },
                  { icon: Users, label: "Fan registrati", value: stats.fans },
                  { icon: FileImage, label: "Post totali", value: stats.posts },
                  { icon: BadgeCheck, label: "Creator verificati", value: stats.verified_creators },
                  { icon: Lock, label: "Contenuti a pagamento", value: stats.locked_posts },
                  { icon: DollarSign, label: "Commissione", value: `${Math.round(stats.commission_rate * 100)}%` },
                ].map((s, i) => (
                  <div key={i} data-testid={`admin-stat-${i}`} className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6">
                    <s.icon className="w-6 h-6 text-[#E91E8C] mb-3" />
                    <p className="text-3xl font-black font-heading">{s.value}</p>
                    <p className="text-xs text-white/50 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
            {tab === "overview" && growth.length > 0 && <AdminGrowthChart data={growth} />}

            {tab === "users" && (
              <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl overflow-hidden" data-testid="admin-users">
                <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_120px_120px_140px] gap-4 px-6 py-3 text-xs uppercase tracking-wider text-white/40 border-b border-white/10">
                  <span>Utente</span><span className="hidden sm:block">Ruolo</span><span>Stato</span><span className="text-right">Azione</span>
                </div>
                {users.map((u) => (
                  <div key={u.id} data-testid={`admin-user-${u.id}`} className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1fr_120px_120px_140px] gap-4 px-6 py-4 items-center border-b border-white/5 last:border-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm truncate">{u.display_name}</span>
                        {u.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#F5C6D6] shrink-0" />}
                      </div>
                      <p className="text-xs text-white/40 truncate">{u.email}</p>
                    </div>
                    <span className="hidden sm:flex items-center gap-1.5 text-xs capitalize text-white/70">
                      {u.role === "creator" ? <Crown className="w-3.5 h-3.5 text-[#E91E8C]" /> : <Users className="w-3.5 h-3.5" />}
                      {u.role}
                    </span>
                    <span className={`text-xs font-medium ${u.suspended ? "text-red-400" : "text-emerald-400"}`}>
                      {u.suspended ? "Sospeso" : "Attivo"}
                    </span>
                    <div className="flex justify-end">
                      <Button
                        onClick={() => toggleSuspend(u)} data-testid={`suspend-btn-${u.id}`}
                        variant="outline" size="sm"
                        className={`rounded-full text-xs gap-1.5 bg-transparent ${u.suspended ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" : "border-red-500/40 text-red-400 hover:bg-red-500/10"}`}
                      >
                        <Ban className="w-3.5 h-3.5" />{u.suspended ? "Riattiva" : "Sospendi"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "moderation" && (
              <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-12 text-center" data-testid="admin-moderation">
                <Flag className="w-10 h-10 text-[#E91E8C] mx-auto mb-4" />
                <h3 className="text-lg font-bold font-heading mb-2">Coda di moderazione</h3>
                <p className="text-white/50 max-w-md mx-auto">Nessun contenuto in attesa di revisione. La segnalazione e la moderazione automatica dei contenuti verranno collegate in una fase successiva.</p>
              </div>
            )}

            {tab === "content" && (
              <div data-testid="admin-content">
                {posts.length === 0 ? (
                  <p className="text-white/50">Nessun contenuto presente.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {posts.map((p) => (
                      <div key={p.id} data-testid={`content-${p.id}`} className="bg-[#1A1A1A] border border-white/10 rounded-2xl overflow-hidden">
                        <div className="relative aspect-square bg-black">
                          {p.media_type === "video" ? (
                            <video controls playsInline preload="metadata" data-testid={`content-video-${p.id}`} className="w-full h-full object-contain bg-black">
                              <source src={mediaUrl(p.media_url)} type="video/mp4" />
                              {p.media_url_alt && <source src={mediaUrl(p.media_url_alt)} type="video/webm" />}
                            </video>
                          ) : (
                            <img src={mediaUrl(p.media_url)} alt={p.caption} className="w-full h-full object-cover" />
                          )}
                          <div className="absolute top-2 left-2 flex gap-2">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 ${p.is_locked ? "bg-[#E91E8C] text-white" : "bg-white/15 text-white backdrop-blur"}`}>
                              {p.is_locked ? <><Lock className="w-3 h-3" /> €{Number(p.price).toFixed(2)}</> : <><Unlock className="w-3 h-3" /> Gratuito</>}
                            </span>
                            {p.media_type === "video" && (
                              <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 bg-black/60 backdrop-blur text-white">
                                <Film className="w-3 h-3" /> Video
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-1.5">
                            <img src={mediaUrl(p.creator_avatar)} alt={p.creator_name} className="w-6 h-6 rounded-full object-cover" />
                            <span className="text-sm font-semibold truncate">{p.creator_name}</span>
                            {p.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#F5C6D6] shrink-0" />}
                          </div>
                          <p className="text-sm text-white/70 mb-2 line-clamp-2">{p.caption}</p>
                          <p className="text-xs text-white/40 mb-3">{fmtDate(p.created_at)}</p>
                          <button onClick={() => openComments(p)} data-testid={`admin-comments-toggle-${p.id}`} className="text-xs text-[#E91E8C] hover:underline mb-2 flex items-center gap-1">
                            <MessageCircle className="w-3.5 h-3.5" /> {commentsFor === p.id ? "Nascondi commenti" : `Commenti (${p.comment_count || 0})`}
                          </button>
                          {commentsFor === p.id && (
                            <div className="mb-3 space-y-1.5 max-h-40 overflow-y-auto" data-testid={`admin-comments-${p.id}`}>
                              {commentList.length === 0 ? (
                                <p className="text-xs text-white/40">Nessun commento.</p>
                              ) : commentList.map((c) => (
                                <div key={c.id} data-testid={`admin-comment-${c.id}`} className="flex items-start gap-2 bg-[#0D0D0D] rounded-lg px-2.5 py-1.5">
                                  <div className="min-w-0">
                                    <span className="text-xs font-semibold">{c.author_name}</span>
                                    <p className="text-xs text-white/60 break-words">{c.text}</p>
                                  </div>
                                  <button onClick={() => delComment(c.id)} data-testid={`admin-delete-comment-${c.id}`} className="ml-auto text-white/30 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" data-testid={`remove-content-${p.id}`} className="w-full rounded-full text-xs gap-1.5 bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10">
                                <Trash2 className="w-3.5 h-3.5" /> Rimuovi contenuto
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#1A1A1A] border-white/10 text-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-heading">Rimuovere questo contenuto?</AlertDialogTitle>
                                <AlertDialogDescription className="text-white/50">
                                  Il post di {p.creator_name} verrà rimosso definitivamente dalla piattaforma. Azione non reversibile.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 text-white">Annulla</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deletePost(p)} data-testid={`confirm-remove-${p.id}`} className="bg-red-500 hover:bg-red-600 text-white">Rimuovi</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "verification" && (
              <div className="space-y-4" data-testid="admin-verification">
                <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                  <ShieldAlert className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-white/70">Materiale sensibile: i documenti d'identità qui mostrati sono riservati. Trattali secondo le normative privacy/KYC.</p>
                </div>
                {verifications.length === 0 && (
                  <p className="text-white/50" data-testid="verif-empty">Nessun creator in attesa di verifica.</p>
                )}
                {verifications.map((v) => (
                  <div key={v.profile_id} data-testid={`verif-review-${v.profile_id}`} className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <img src={mediaUrl(v.avatar_url)} alt={v.display_name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{v.display_name}</p>
                        <p className="text-xs text-white/40">
                          {v.verification_status === "rejected" ? "Rifiutato · in attesa di reinvio" : "In verifica"}
                        </p>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${v.verification_status === "rejected" ? "bg-red-500/15 text-red-400" : "bg-amber-400/10 text-amber-300"}`}>
                        {v.verification_status === "rejected" ? "Rifiutato" : "In verifica"}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                      {[
                        { key: "id_front", label: "Documento (fronte)" },
                        { key: "id_back", label: "Documento (retro)" },
                        { key: "selfie", label: "Selfie con documento" },
                      ].map((d) => {
                        const url = v.docs?.[d.key];
                        const isPdf = typeof url === "string" && url.toLowerCase().includes(".pdf");
                        return (
                          <div key={d.key}>
                            <div className="aspect-video rounded-xl overflow-hidden border border-white/10 bg-black flex items-center justify-center">
                              {!url ? (
                                <span className="text-xs text-white/30">—</span>
                              ) : isPdf ? (
                                <a href={mediaUrl(url)} target="_blank" rel="noreferrer" data-testid={`verif-doc-${v.profile_id}-${d.key}`} className="text-xs text-[#E91E8C] flex flex-col items-center gap-1">
                                  <FileText className="w-6 h-6" /> Apri PDF
                                </a>
                              ) : (
                                <img src={mediaUrl(url)} alt={d.label} data-testid={`verif-doc-${v.profile_id}-${d.key}`} className="w-full h-full object-cover" />
                              )}
                            </div>
                            <p className="text-[11px] text-white/50 mt-1.5 text-center">{d.label}</p>
                          </div>
                        );
                      })}
                    </div>
                    {v.verification_status === "rejected" && v.rejection_reason && (
                      <p className="text-xs text-red-400 mb-3">Ultimo rifiuto: {v.rejection_reason}</p>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => approveVerif(v)} data-testid={`verif-approve-${v.profile_id}`}
                        size="sm" className="rounded-full text-xs gap-1.5 bg-[#E91E8C] hover:bg-[#D8157B] text-white"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approva verifica
                      </Button>
                      <Button
                        onClick={() => { setRejectFor(v); setRejectReason(""); }} data-testid={`verif-reject-${v.profile_id}`}
                        variant="outline" size="sm" className="rounded-full text-xs gap-1.5 bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10"
                      >
                        <Ban className="w-3.5 h-3.5" /> Rifiuta verifica
                      </Button>
                    </div>
                  </div>
                ))}

                <AlertDialog open={!!rejectFor} onOpenChange={(o) => { if (!o) setRejectFor(null); }}>
                  <AlertDialogContent className="bg-[#1A1A1A] border-white/10 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-heading">Rifiuta verifica</AlertDialogTitle>
                      <AlertDialogDescription className="text-white/50">
                        Indica il motivo del rifiuto per {rejectFor?.display_name}. Il creator lo vedrà nel proprio profilo e potrà reinviare i documenti.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <textarea
                      value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
                      data-testid="verif-reject-reason" placeholder="Es. documento illeggibile, selfie non corrispondente…"
                      className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl p-3 text-sm focus:border-[#E91E8C] focus:outline-none"
                    />
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 text-white">Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={rejectVerif} data-testid="verif-reject-confirm" className="bg-red-500 hover:bg-red-600 text-white">Rifiuta</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}

            {tab === "finance" && (
              <div data-testid="admin-finance">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Volume transato", value: `€${Number(stats.transaction_volume).toFixed(2)}`, testid: "fin-volume" },
                    { label: `Commissioni (${Math.round(stats.commission_rate * 100)}%)`, value: `€${Number(stats.commission_earned).toFixed(2)}`, testid: "fin-commission", highlight: true },
                    { label: "Payout ai creator", value: `€${Number(stats.creator_payout).toFixed(2)}`, testid: "fin-payout" },
                    { label: "Transazioni totali", value: stats.transaction_count, testid: "fin-count" },
                  ].map((s, i) => (
                    <div key={i} data-testid={s.testid} className={`rounded-2xl p-6 border ${s.highlight ? "bg-gradient-to-br from-[#E91E8C]/20 to-[#0D0D0D] border-[#E91E8C]/40" : "bg-[#1A1A1A] border-white/10"}`}>
                      <p className={`text-3xl font-black font-heading ${s.highlight ? "text-[#E91E8C]" : ""}`}>{s.value}</p>
                      <p className="text-xs text-white/50 mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-[#E91E8C]/10 border border-[#E91E8C]/30 rounded-2xl p-5 flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#E91E8C] mt-0.5 shrink-0" />
                  <p className="text-sm text-white/70">Dati calcolati da transazioni simulate (mock). L'integrazione di un processore di pagamento reale arriverà in una fase successiva.</p>
                </div>

                {/* Fiscal data per creator */}
                <div className="mt-6" data-testid="admin-fiscal">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-bold font-heading flex items-center gap-2"><FileText className="w-4 h-4 text-[#E91E8C]" /> Dati fiscali creator</h3>
                    <Button
                      onClick={exportBilling} data-testid="export-billing-btn" size="sm"
                      className="rounded-full bg-[#E91E8C] hover:bg-[#D8157B] text-xs gap-1.5"
                    >
                      <FileText className="w-3.5 h-3.5" /> Esporta CSV
                    </Button>
                  </div>
                  <div className="rounded-2xl border border-white/10 overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm min-w-[560px]">
                      <thead>
                        <tr className="bg-[#0D0D0D] text-white/40 text-xs uppercase tracking-wider">
                          <th className="text-left px-4 py-2.5">Creator</th>
                          <th className="text-left px-4 py-2.5">Modalità</th>
                          <th className="text-left px-4 py-2.5">Intestatario</th>
                          <th className="text-left px-4 py-2.5">P.IVA / CF</th>
                          <th className="text-left px-4 py-2.5">IBAN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billing.map((b) => {
                          const d = b.billing || {};
                          return (
                            <tr key={b.creator_id} data-testid={`fiscal-row-${b.creator_id}`} className="border-t border-white/5">
                              <td className="px-4 py-3 font-medium">{b.display_name}</td>
                              <td className="px-4 py-3">
                                {b.mode === "vat" ? (
                                  <span className="text-xs font-semibold text-[#E91E8C] bg-[#E91E8C]/10 border border-[#E91E8C]/30 rounded-full px-2 py-0.5">A · P.IVA</span>
                                ) : b.mode === "occasional" ? (
                                  <span className="text-xs font-semibold text-[#F5C6D6] bg-[#F5C6D6]/10 border border-[#F5C6D6]/30 rounded-full px-2 py-0.5">B · Occasionale</span>
                                ) : (
                                  <span className="text-xs text-white/30">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-white/70">{d.legal_name || d.name || "—"}</td>
                              <td className="px-4 py-3 text-white/70">{d.vat_number || d.tax_code || "—"}</td>
                              <td className="px-4 py-3 text-white/50 text-xs">{d.iban || "—"}</td>
                            </tr>
                          );
                        })}
                        {billing.length === 0 && (
                          <tr><td colSpan={5} className="px-4 py-6 text-center text-white/40">Nessun dato fiscale disponibile.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
